import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  deleteCurrentUserAccount,
  getProfileFromUser,
  requestPasswordReset,
  signInWithEmail,
  signOutUser,
  signUpWithEmail,
  updateUserPassword,
  updateUserProfile,
} from '../Services/authService';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);
const isRecoveryLocation = () => {
  if (typeof window === 'undefined') return false;

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);
  return hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery';
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(isRecoveryLocation());

  const applySession = useCallback(async (nextSession) => {
    setSession(nextSession);

    const nextUser = nextSession?.user ?? null;
    setUser(nextUser);
    setProfile(nextUser ? getProfileFromUser(nextUser) : null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let active = true;

    const initialize = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (active) {
          await applySession(data.session ?? null);
        }
      } catch (error) {
        console.error('Failed to initialize auth session:', error);
        if (active) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setIsLoading(false);
        }
      }
    };

    initialize();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_OUT') {
        setIsPasswordRecovery(false);
      }
      void applySession(nextSession ?? null);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = useCallback(async ({ email, password }) => {
    const { data } = await signInWithEmail({ email, password });
    await applySession(data.session ?? null);
    return data;
  }, [applySession]);

  const signUp = useCallback(async ({ email, password, fullName, firstName, lastName, city, state, country }) => {
    const { data } = await signUpWithEmail({ email, password, fullName, firstName, lastName, city, state, country });
    await applySession(data.session ?? null);
    return data;
  }, [applySession]);

  const signOut = useCallback(async () => {
    await signOutUser();
    await applySession(null);
  }, [applySession]);

  const updateProfile = useCallback(async ({ fullName, firstName, lastName, city, state, country }) => {
    const { data } = await updateUserProfile({ fullName, firstName, lastName, city, state, country });
    const updatedUser = data.user ?? user;
    setUser(updatedUser);
    setProfile(updatedUser ? getProfileFromUser(updatedUser) : null);
    return data;
  }, [user]);

  const sendPasswordReset = useCallback(async (email) => {
    await requestPasswordReset(email);
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { data } = await updateUserPassword(password);
    const updatedUser = data.user ?? user;
    setUser(updatedUser);
    setProfile(updatedUser ? getProfileFromUser(updatedUser) : null);
    setIsPasswordRecovery(false);
    if (typeof window !== 'undefined' && (window.location.hash || window.location.search)) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    return data;
  }, [user]);

  const deleteAccount = useCallback(async () => {
    await deleteCurrentUserAccount();
    setSession(null);
    setUser(null);
    setProfile(null);
    setIsPasswordRecovery(false);
  }, []);

  const value = useMemo(() => ({
    session,
    user,
    profile,
    isLoading,
    isAuthenticated: !!user,
    isPasswordRecovery,
    signIn,
    signUp,
    signOut,
    updateProfile,
    sendPasswordReset,
    updatePassword,
    deleteAccount,
  }), [
    session,
    user,
    profile,
    isLoading,
    isPasswordRecovery,
    signIn,
    signUp,
    signOut,
    updateProfile,
    sendPasswordReset,
    updatePassword,
    deleteAccount,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
